import { Action, createReducer, on } from '@ngrx/store';
import { HttpError } from '@rd/types';
import { Promocode, PromocodeUsage } from '@rd/types/promocode.type';
import { toObject } from '@rd/utils/array.utils';
import {
  AddCartItem,
  AddCartItemFail,
  AddCartItemSuccess,
  AddCartPromocode,
  AddCartPromocodeFail,
  AddCartPromocodeSuccess,
  LoadCart,
  LoadCartAfterAddingItem,
  LoadCartFail,
  LoadCartSuccess,
  RemoveCartExpiredPromocode,
  RemoveCartItems,
  RemoveCartItemsFail,
  RemoveCartPromocode,
  RemoveCartPromocodeFail,
  RemoveCartPromocodeSuccess,
  ResetCartPromocodeError,
  UpdateCartItemQuantity,
  UpdateCartItemQuantityFail,
} from '../actions/cart-item.actions';
import { CartItem } from '../types/cart-item.type';
import { Cart } from '../types/cart.type';

export interface CartItemState {
  cartItems: {
    [key: string]: Partial<CartItem>;
  };
  promocode: {
    value: Promocode | null;
    usage: PromocodeUsage | null;
    post: {
      error: HttpError | null;
      loading: boolean;
    };
  };
  index: {
    error: HttpError | null;
    loading: boolean;
    hasLoaded: boolean;
  };
  post: {
    error: HttpError | null;
    loading: boolean;
  };
}

const initialState: CartItemState = {
  cartItems: {},
  promocode: {
    value: null,
    usage: null,
    post: {
      error: null,
      loading: false,
    },
  },
  index: {
    error: null,
    loading: false,
    hasLoaded: false,
  },
  post: {
    error: null,
    loading: false,
  },
};

const featureReducer = createReducer(
  initialState,
  on(RemoveCartItems, UpdateCartItemQuantity, (state: CartItemState) => {
    return {
      ...state,
      index: {
        ...state.index,
        error: null,
        loading: true,
      },
    };
  }),
  on(
    RemoveCartItemsFail,
    UpdateCartItemQuantityFail,
    (state: CartItemState, payload: HttpError & { type: string }) => {
      const { type, ...httpError } = payload;
      return {
        ...state,
        index: {
          ...state.index,
          error: httpError,
          loading: false,
        },
      };
    },
  ),
  on(AddCartItem, (state: CartItemState) => {
    return {
      ...state,
      post: {
        error: null,
        loading: true,
      },
    };
  }),
  on(AddCartItemSuccess, (state: CartItemState) => {
    return {
      ...state,
      post: {
        ...state.post,
        loading: false,
      },
    };
  }),
  on(
    AddCartItemFail,
    (
      state: CartItemState,
      payload: Record<string, string | number | HttpError> &
        Required<{
          error: HttpError;
          type: string;
        }>,
    ) => {
      const { error } = payload;
      return {
        ...state,
        post: {
          error,
          loading: false,
        },
      };
    },
  ),
  on(LoadCart, LoadCartAfterAddingItem, (state: CartItemState) => {
    return {
      ...state,
      index: {
        ...state.index,
        error: null,
        loading: true,
      },
    };
  }),
  on(LoadCartSuccess, (state: CartItemState, payload: { cart: Cart }) => {
    const { cartItems, promocode, promocodeUsage } = payload.cart;
    return {
      ...state,
      cartItems: {
        ...toObject(cartItems, 'id'),
      },
      promocode: {
        ...state.promocode,
        value: promocode,
        usage: promocodeUsage,
      },
      index: {
        error: null,
        loading: false,
        hasLoaded: true,
      },
    };
  }),
  on(
    LoadCartFail,
    (state: CartItemState, payload: HttpError & { type: string }) => {
      const { type, ...httpError } = payload;
      return {
        ...state,
        index: {
          ...state.index,
          error: httpError,
          loading: false,
        },
      };
    },
  ),
  on(AddCartPromocode, (state: CartItemState) => {
    return {
      ...state,
      promocode: {
        ...state.promocode,
        post: {
          error: state.promocode.post.error,
          loading: true,
        },
      },
    };
  }),
  on(
    AddCartPromocodeSuccess,
    (state: CartItemState, payload: { cart: Cart }) => {
      const { cartItems, promocode, promocodeUsage } = payload.cart;
      return {
        ...state,
        cartItems: {
          ...toObject(cartItems, 'id'),
        },
        promocode: {
          value: promocode,
          usage: promocodeUsage,
          post: {
            error: null,
            loading: false,
          },
        },
      };
    },
  ),
  on(
    AddCartPromocodeFail,
    (state: CartItemState, payload: HttpError & { type: string }) => {
      const { type, ...httpError } = payload;
      return {
        ...state,
        promocode: {
          ...state.promocode,
          post: {
            error: httpError,
            loading: false,
          },
        },
      };
    },
  ),
  on(RemoveCartPromocode, (state: CartItemState) => {
    return {
      ...state,
      promocode: {
        ...state.promocode,
        post: {
          error: null,
          loading: true,
        },
      },
    };
  }),
  // @ts-ignore
  on(RemoveCartExpiredPromocode, (state: CartItemState) => {
    return {
      ...state,
      promocode: {
        ...state.promocode,
        usage: {
          ...state.promocode.usage,
          status: '',
        },
      },
    };
  }),
  on(RemoveCartPromocodeSuccess, (state: CartItemState) => {
    return {
      ...state,
      promocode: {
        value: null,
        usage: null,
        post: {
          error: null,
          loading: false,
        },
      },
    };
  }),
  on(
    RemoveCartPromocodeFail,
    (state: CartItemState, payload: HttpError & { type: string }) => {
      const { type, ...httpError } = payload;
      return {
        ...state,
        promocode: {
          ...state.promocode,
          post: {
            loading: false,
            error: httpError,
          },
        },
      };
    },
  ),
  on(ResetCartPromocodeError, (state: CartItemState) => {
    return {
      ...state,
      promocode: {
        ...state.promocode,
        post: {
          loading: false,
          error: null,
        },
      },
    };
  }),
);

export function cartItemReducer(
  state: CartItemState | undefined,
  action: Action,
): CartItemState {
  return featureReducer(state, action);
}
